import { useRef, useEffect, useCallback } from 'react'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage, useGetCurrentGame } from 'src/store/game'
import { isMarbleGame } from 'src/types/domain/Game'
import { isCCTVGameType } from 'src/utils/gameTypeUtils'
import TimesIcon from 'src/app/assets/icons/ico-times.svg'
import MusicIcon from 'src/app/assets/icons/ico-music.svg'
import MusicSlashIcon from 'src/app/assets/icons/ico-music-slash.svg'
import MicrophoneIcon from 'src/app/assets/icons/ico-microphone-lines.svg'
import MicrophoneSlashIcon from 'src/app/assets/icons/ico-microphone-lines-slash.svg'
import WaveformIcon from 'src/app/assets/icons/ico-waveform-lines.svg'
import { useGetAllSoundSettings, useSoundActions } from 'src/store/sound'
import { Button } from 'src/components/Button'
import { VolumeControl } from './VolumeControl'

// Levels restored when un-muting a channel whose volume was dragged to 0,
// so the toggle actually makes sound again. Mirrors the store defaults.
const DEFAULT_VOLUME = { master: 0.8, race: 1.0, effects: 0.5, music: 0.5 } as const

export function AudioControlsDialog({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage)
  const { volumes, muted } = useGetAllSoundSettings()
  const actions = useSoundActions()

  // CCTV games (irl, irl_alt, duck_river, snow_run, social_betting)
  // stream their music directly inside the livestream audio — there's no
  // separate Music track for this channel to control, so hide the Music
  // control on those games (it's auto-muted/unmuted by game type in
  // Music.tsx, which stays mounted across game navigation). QA-60177.
  const currentGame = useGetCurrentGame()
  const gameType = isMarbleGame(currentGame) ? currentGame.track.gameType : 'marbles'
  const gameHasMusic = !isCCTVGameType(gameType)

  // Derived on/off so each toggle reflects *effective* audibility rather
  // than a standalone mute flag that drifts from the slider and Master
  // (QA-60187 / QA-60200 / QA-60202):
  //   - a channel is off if Master is silent, the channel is muted, or
  //     its own volume is 0
  //   - Master is off if it's muted/0, or every visible channel is off
  const masterSilent = muted.master || volumes.master === 0
  const ownOff = (ch: 'race' | 'effects' | 'music') => muted[ch] || volumes[ch] === 0
  const channelDisplayMuted = (ch: 'race' | 'effects' | 'music') => masterSilent || ownOff(ch)
  const visibleChannels: Array<'race' | 'effects' | 'music'> = gameHasMusic
    ? ['race', 'effects', 'music']
    : ['race', 'effects']
  const masterDisplayMuted = masterSilent || visibleChannels.every(ownOff)

  const CHANNEL_UNMUTE = {
    race: actions.unmuteRace,
    effects: actions.unmuteEffects,
    music: actions.unmuteMusic
  }
  const CHANNEL_MUTE = {
    race: actions.muteRace,
    effects: actions.muteEffects,
    music: actions.muteMusic
  }
  const CHANNEL_SET_VOLUME = {
    race: actions.changeRaceVolume,
    effects: actions.changeEffectsVolume,
    music: actions.changeMusicVolume
  }

  const turnMasterOn = () => {
    actions.unmuteMaster()
    if (volumes.master === 0) actions.changeMasterVolume(DEFAULT_VOLUME.master)
  }
  const toggleMaster = () => {
    if (masterDisplayMuted) {
      // Master is the aggregate switch: bring ALL sound back, including
      // channels the player muted individually — not just ones Master
      // silenced. Restore any channel sitting at 0 volume too.
      turnMasterOn()
      visibleChannels.forEach((ch) => {
        if (muted[ch]) CHANNEL_UNMUTE[ch]()
        if (volumes[ch] === 0) CHANNEL_SET_VOLUME[ch](DEFAULT_VOLUME[ch])
      })
    } else {
      actions.muteMaster()
    }
  }
  const toggleChannel = (ch: 'race' | 'effects' | 'music') => {
    if (channelDisplayMuted(ch)) {
      // Genuinely turn it on: restore Master if it was the blocker,
      // unmute the channel, and lift volume off 0 if it sat there.
      if (masterSilent) turnMasterOn()
      CHANNEL_UNMUTE[ch]()
      if (volumes[ch] === 0) CHANNEL_SET_VOLUME[ch](DEFAULT_VOLUME[ch])
    } else {
      CHANNEL_MUTE[ch]()
    }
  }
  // Plain volume setters. We deliberately do NOT auto-unmute on a
  // slider change: VolumeControl echoes its initial volume back through
  // setVolume on mount, so unmuting here would wipe every muted channel
  // each time the dialog is reopened. The 0%→"off" display is handled by
  // the derived state above; the toggle button owns mute/unmute.
  const setMasterVolume = (v: number) => actions.changeMasterVolume(v)
  const setChannelVolume = (ch: 'race' | 'effects' | 'music', v: number) =>
    CHANNEL_SET_VOLUME[ch](v)

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Element
      const isVolumeButton = target.closest('.game-toolbar-btn')
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        !isVolumeButton &&
        isOpen
      ) {
        onClose()
      }
    },
    [isOpen, onClose]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClickOutside])

  // While open, flag the body so CSS can drop the mobile
  // `.marbles-race__bottom` (z-index 900) below the toolbar so this
  // panel isn't painted over on small screens (QA-60161).
  useEffect(() => {
    if (!isOpen) return
    document.body.classList.add('audio-panel-open')
    return () => document.body.classList.remove('audio-panel-open')
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="audio-controls-panel" ref={panelRef}>
      <div className="panel-content_section">
        <div className="panel-header">
          <h3>{t('Audio Controls')}</h3>
          <Button className="close-button" onClick={onClose} useDefaultClasses={false}>
            <TimesIcon />
          </Button>
        </div>
        <div className="panel-audio-controls">
          <div className="panel-audio-controls__section">
            <VolumeControl
              isMuted={masterDisplayMuted}
              label={t('Master')}
              setVolume={setMasterVolume}
              toggleMute={toggleMaster}
              volume={volumes.master}
            />
          </div>
          <div className="panel-audio-controls__section">
            <VolumeControl
              isMuted={channelDisplayMuted('race')}
              label={t('Race')}
              muteIcon={MicrophoneSlashIcon}
              setVolume={(v) => setChannelVolume('race', v)}
              toggleMute={() => toggleChannel('race')}
              volume={volumes.race}
              volumeIcon={MicrophoneIcon}
            />
            <VolumeControl
              isMuted={channelDisplayMuted('effects')}
              label={t('Sounds')}
              muteIcon={WaveformIcon}
              setVolume={(v) => setChannelVolume('effects', v)}
              toggleMute={() => toggleChannel('effects')}
              volume={volumes.effects}
              volumeIcon={WaveformIcon}
            />
            {gameHasMusic && (
              <VolumeControl
                isMuted={channelDisplayMuted('music')}
                label={t('Music')}
                muteIcon={MusicSlashIcon}
                setVolume={(v) => setChannelVolume('music', v)}
                toggleMute={() => toggleChannel('music')}
                volume={volumes.music}
                volumeIcon={MusicIcon}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
